import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, Minus } from 'lucide-react'
import { motion } from 'framer-motion'

interface FAQItem {
  id: string
  question: string
  answer: string
  category: string
}

const faqData: FAQItem[] = [
  // General Card-to-Crypto
  {
    id: '1',
    category: 'General',
    question: 'What is card-to-crypto payment processing?',
    answer:
      'Card-to-crypto payment processing allows customers to pay with traditional credit or debit cards while merchants receive cryptocurrency. Our platform seamlessly converts card payments into crypto, eliminating the need for customers to own cryptocurrency while giving merchants the benefits of digital currency settlements.',
  },
  {
    id: '2',
    category: 'General',
    question: "How does PexiBlock's card-to-crypto solution work?",
    answer:
      'When a customer makes a payment with their card, our system processes the transaction through traditional payment networks, converts the fiat currency to cryptocurrency in real-time, and settles the crypto directly to your wallet. The entire process is transparent to the customer and takes seconds to complete.',
  },
  {
    id: '3',
    category: 'General',
    question: 'What cryptocurrencies do you support?',
    answer:
      'We support major cryptocurrencies including Bitcoin (BTC), Ethereum (ETH), USD Coin (USDC), Tether (USDT), and other popular stablecoins. We continuously add support for new cryptocurrencies based on market demand and stability.',
  },

  // Integration & Technical
  {
    id: '4',
    category: 'Integration',
    question: 'How long does it take to integrate PexiBlock?',
    answer:
      'Most integrations can be completed within 24-48 hours. Our RESTful API and comprehensive documentation make it easy for developers to implement. We also provide SDKs for popular programming languages and e-commerce platforms.',
  },
  {
    id: '5',
    category: 'Integration',
    question: 'Do you provide test environments?',
    answer:
      'Yes, we provide a full sandbox environment that mirrors our production system. You can test all features, including payment processing, webhook notifications, and settlement flows without processing real transactions or incurring fees.',
  },
  {
    id: '6',
    category: 'Integration',
    question: 'What e-commerce platforms do you support?',
    answer:
      'We support major e-commerce platforms including Shopify, WooCommerce, Magento, BigCommerce, and custom-built solutions through our API. Our plugins and extensions make integration straightforward for popular platforms.',
  },

  // Fees & Pricing
  {
    id: '7',
    category: 'Fees',
    question: 'What are your processing fees?',
    answer:
      'Our processing fees are competitive and transparent, typically ranging from 5.5% to 7.5% per transaction depending on your processing volume and settlement preferences. There are no setup fees, monthly minimums, or hidden charges.',
  },
  {
    id: '8',
    category: 'Fees',
    question: 'Are there any setup or monthly fees?',
    answer:
      "No, we don't charge setup fees or monthly maintenance fees. You only pay for successful transactions. This allows businesses of all sizes to benefit from crypto payments without upfront costs or ongoing commitments.",
  },
  {
    id: '9',
    category: 'Fees',
    question: 'How do conversion rates work?',
    answer:
      'We use real-time market rates from multiple cryptocurrency exchanges to ensure competitive conversion rates. The exchange rate is locked at the moment of transaction to protect both merchants and customers from volatility during processing.',
  },

  // Security & Compliance
  {
    id: '10',
    category: 'Security',
    question: 'Is PexiBlock PCI DSS compliant?',
    answer:
      'Yes, we maintain PCI DSS Level 1 compliance, the highest level of payment security certification. We also implement additional security measures including end-to-end encryption, secure key management, and regular security audits.',
  },
  {
    id: '11',
    category: 'Security',
    question: 'How do you handle customer data protection?',
    answer:
      'We follow strict data protection protocols and comply with GDPR, CCPA, and other privacy regulations. Customer card data is tokenized and encrypted, never stored in plain text, and we use secure, isolated environments for all data processing.',
  },
  {
    id: '12',
    category: 'Security',
    question: 'What KYC/AML compliance measures do you have?',
    answer:
      'We implement comprehensive KYC (Know Your Customer) and AML (Anti-Money Laundering) procedures in compliance with international regulations. This includes identity verification, transaction monitoring, and reporting suspicious activities to relevant authorities.',
  },

  // Settlements & Payouts
  {
    id: '13',
    category: 'Settlement',
    question: 'How fast are crypto settlements?',
    answer:
      'Crypto settlements typically occur within minutes to a few hours, depending on the blockchain network. This is significantly faster than traditional payment methods which can take 2-5 business days for settlement.',
  },
  {
    id: '14',
    category: 'Settlement',
    question: 'Can I choose which crypto to receive?',
    answer:
      'Yes, you can configure your preferred settlement cryptocurrency. You can also set up multiple wallets for different cryptocurrencies or split settlements across multiple coins based on percentages you define.',
  },
  {
    id: '15',
    category: 'Settlement',
    question: 'Do you handle cryptocurrency volatility?',
    answer:
      'We offer multiple options to manage volatility: instant conversion to stablecoins, hedging mechanisms, or accepting the market rate at settlement time. You can choose the approach that best fits your risk tolerance and business model.',
  },

  // Customer Experience
  {
    id: '16',
    category: 'Customer Experience',
    question: 'Do customers need cryptocurrency knowledge to pay?',
    answer:
      "No, customers use their regular credit or debit cards just like any other online purchase. They don't need to understand cryptocurrency, own digital wallets, or navigate complex crypto interfaces. The process is identical to traditional card payments.",
  },
  {
    id: '17',
    category: 'Customer Experience',
    question: 'What happens if a payment fails?',
    answer:
      "If a card payment fails, customers receive immediate feedback and can retry with a different card or payment method. Failed transactions don't incur charges, and our system provides clear error messages to help resolve issues quickly.",
  },
  {
    id: '18',
    category: 'Customer Experience',
    question: 'Can customers get refunds?',
    answer:
      "Yes, refunds are processed back to the customer's original payment method (their card). Since cryptocurrency transactions are irreversible, we handle refunds through the traditional payment network, ensuring customers receive their money back in the currency they paid with.",
  },

  // Business Benefits
  {
    id: '19',
    category: 'Business',
    question: 'Why choose crypto settlements over traditional payments?',
    answer:
      'Crypto settlements offer several advantages: no chargebacks, faster settlement times, lower fees for international transactions, access to global markets, reduced fraud risk, and protection against payment reversals. Plus, you can benefit from potential cryptocurrency appreciation.',
  },
  {
    id: '20',
    category: 'Business',
    question: 'Can I still accept traditional payments alongside crypto?',
    answer:
      'Absolutely. Our solution can work alongside your existing payment processors. You can offer customers multiple payment options while taking advantage of crypto benefits for transactions you choose to process through our platform.',
  },
]

const categories = [
  'All',
  'General',
  'Integration',
  'Fees',
  'Security',
  'Settlement',
  'Customer Experience',
  'Business',
]

export default function FAQ() {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [openItems, setOpenItems] = useState<string[]>([])

  const filteredFAQs =
    selectedCategory === 'All' ? faqData : faqData.filter(faq => faq.category === selectedCategory)

  const toggleItem = (id: string) => {
    setOpenItems(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]))
  }

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  return (
    <motion.div
      className="min-h-screen bg-gray-900"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute rounded-full -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/30 to-blue-200/30 blur-3xl animate-pulse-slow"></div>
        <div
          className="absolute rounded-full -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-200/30 to-orange-200/30 blur-3xl animate-pulse-slow"
          style={{ animationDelay: '1s' }}
        ></div>
        <div className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full top-1/2 left-1/2 w-96 h-96 bg-gradient-to-r from-orange-100/20 to-blue-100/20 blur-3xl animate-float"></div>
      </div>

      <div className="relative z-10 max-w-5xl px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <motion.div className="mb-8" variants={fadeInUp}>
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-orange-400 transition-colors hover:text-orange-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div className="mb-12 text-center" variants={fadeInUp}>
          <h1 className="mb-4 text-4xl font-bold text-white">Frequently Asked Questions</h1>
          <p className="max-w-3xl mx-auto text-xl text-gray-300">
            Everything you need to know about card-to-crypto payment processing with PexiBlock
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div className="mb-8" variants={fadeInUp}>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === category
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-800 text-gray-300 hover:bg-slate-700 hover:text-white'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Items */}
        <motion.div className="space-y-4" variants={staggerContainer}>
          {filteredFAQs.map(faq => (
            <motion.div
              key={faq.id}
              className="overflow-hidden border rounded-lg bg-slate-800/50 backdrop-blur-sm border-slate-700/50"
              variants={fadeInUp}
            >
              <button
                onClick={() => toggleItem(faq.id)}
                className="flex items-center justify-between w-full px-6 py-4 text-left transition-colors hover:bg-slate-700/30"
              >
                <div className="flex-1">
                  <div className="flex items-center mb-1 space-x-3">
                    <span className="px-2 py-1 text-xs text-orange-400 rounded-full bg-orange-500/20">
                      {faq.category}
                    </span>
                  </div>
                  <h3 className="pr-4 text-lg font-semibold text-white">{faq.question}</h3>
                </div>
                {openItems.includes(faq.id) ? (
                  <Minus className="flex-shrink-0 w-5 h-5 text-orange-400" />
                ) : (
                  <Plus className="flex-shrink-0 w-5 h-5 text-orange-400" />
                )}
              </button>

              {openItems.includes(faq.id) && (
                <div className="px-6 pb-4">
                  <div className="pt-2 border-t border-slate-700/50">
                    <p className="leading-relaxed text-gray-300">{faq.answer}</p>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          className="p-8 mt-12 border bg-gradient-to-r from-orange-500/10 to-blue-500/10 rounded-2xl border-orange-500/20"
          variants={fadeInUp}
        >
          <div className="text-center">
            <h2 className="mb-4 text-2xl font-bold text-white">Still have questions?</h2>
            <p className="mb-6 text-gray-300">
              Our team is here to help you with any questions about card-to-crypto payments
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/contact"
                className="px-6 py-3 font-semibold text-white transition-colors bg-orange-500 rounded-lg hover:bg-orange-600"
              >
                Contact Support
              </Link>
              <a
                href="mailto:paul@pexipay.com"
                className="px-6 py-3 font-semibold text-white transition-colors rounded-lg bg-slate-700 hover:bg-slate-600"
              >
                Email Us Directly
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
